package songwritersnotebook.app.utils

import kotlinx.browser.window
import org.khronos.webgl.ArrayBuffer
import kotlin.js.Promise
import kotlin.math.abs
import kotlinx.coroutines.await

actual object WaveformGenerator {
    actual suspend fun generateWaveform(filePath: String): WaveformResult {
        var audioContext: AudioContext? = null
        return try {
            val buffer = if (filePath.startsWith("http") || filePath.startsWith("blob:") || filePath.startsWith("/")) {
                val bytes = NetworkUtils().fetchAuthenticated(filePath)
                val uint8Array = org.khronos.webgl.Uint8Array(
                    (bytes.asDynamic() as org.khronos.webgl.Int8Array).buffer,
                    (bytes.asDynamic() as org.khronos.webgl.Int8Array).byteOffset,
                    (bytes.asDynamic() as org.khronos.webgl.Int8Array).length
                )
                uint8Array.buffer.slice(uint8Array.byteOffset, uint8Array.byteOffset + uint8Array.byteLength)
            } else {
                val file = songwritersnotebook.platform.getPlatformFileSystem().getFile(filePath)
                val bytes = file.readBytes()
                val uint8Array = org.khronos.webgl.Uint8Array(bytes.size)
                for (i in bytes.indices) {
                    uint8Array.asDynamic()[i] = bytes[i]
                }
                uint8Array.buffer
            }
            
            val context = js("new (window.AudioContext || window.webkitAudioContext)()") as AudioContext
            audioContext = context
            val audioBuffer = (context.decodeAudioData(buffer as ArrayBuffer) as Promise<AudioBuffer>).await()
            
            val duration = audioBuffer.duration
            val samples = audioBuffer.length
            val sampleRate = audioBuffer.sampleRate
            
            // Aim for 1 point per 10ms, same as Android
            val totalPoints = (duration * 100).toInt().coerceAtLeast(1)
            val samplesPerPoint = (samples / totalPoints).coerceAtLeast(1)
            
            val channelData = audioBuffer.getChannelData(0).asDynamic()
            val points = mutableListOf<Float>()
            
            for (i in 0 until totalPoints) {
                var max = 0f
                val start = i * samplesPerPoint
                val end = ((i + 1) * samplesPerPoint).coerceAtMost(samples)
                for (j in start until end) {
                    val value = abs(channelData[j] as Float)
                    if (value > max) max = value
                }
                points.add(max * 100f) // Scale to 0..100 to match Android
            }
            
            WaveformResult(points, (duration * 1000).toLong())
        } catch (e: Exception) {
            println("WaveformGenerator: Error generating waveform for $filePath: $e")
            WaveformResult(emptyList(), 0L)
        } finally {
            audioContext?.close()
        }
    }
}

// Helper extension removed as we imported standard await
